// 奇门 AI 求测服务：与 baziAiService 同构（baziSessions→qimenSessions，
// bazi-ask→qimen-ask，额外支持 questionCategory 参数）。
import { type User } from 'firebase/auth';

export interface QimenChatMessage {
  role: 'user' | 'assistant';
  content: any;
  mode?: 'reading' | 'ask';
  ts?: number;
  error?: string;
}

export interface QimenReadingData {
  yongShen?: string;
  selfPalace?: string;
  matterPalace?: string;
  palaceRead?: string;
  patterns?: string;
  timing?: string;
  advice?: string;
  summary?: string;
  disclaimer?: string;
  analysis?: string; // 问答模式
}

export interface QimenSessionSummary {
  id: string;
  title: string;
  solarText: string;
  yinYang: string;
  ju: string | number;
  messagesCount: number;
  updatedAt: string;
}

export interface QimenSessionFull {
  id: string;
  title?: string;
  chart: any;
  messages: QimenChatMessage[];
  createdAt?: string;
  updatedAt?: string;
}

export const QIMEN_API_BASE: string =
  ((import.meta as any).env?.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ||
  'https://us-central1-meihua-16427.cloudfunctions.net';

async function apiFetch(path: string, init: RequestInit & { token: string }): Promise<any> {
  const response = await fetch(`${QIMEN_API_BASE}/api${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${init.token}` }
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  if (!response.ok) {
    const err = new Error(
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.error === 'string' && data.error) ||
      `请求失败（${response.status}）`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return data;
}

const tokenOf = async (user: User | null): Promise<string> => {
  if (!user) throw new Error('未登录');
  return user.getIdToken();
};

export async function askQimen(
  user: User | null,
  payload: { mode: 'reading' | 'ask'; question?: string; category?: string; chart: any; analysis?: any; history?: QimenChatMessage[]; sessionId?: string | null }
): Promise<{ data: QimenReadingData; sessionId: string | null; title: string; model?: string }> {
  const body: any = { mode: payload.mode, chart: payload.chart, history: payload.history ?? [] };
  if (payload.sessionId) body.sessionId = payload.sessionId;
  if (payload.category) body.category = payload.category;
  // 交叉分析（用神落宫 / 宫位生克 / 十干克应 / 四害 / 应期线索）随盘发送，服务端排进【奇门局数据】
  if (payload.analysis) {
    const slim = {
      category: payload.analysis.category,
      categoryLabel: payload.analysis.categoryLabel,
      monthWx: payload.analysis.monthWx,
      yong: (payload.analysis.yong || []).map((y: any) => ({ role: y.role, symbol: y.symbol, palaces: y.palaces, vias: y.vias })),
      axis: payload.analysis.axis,
      keyPalaces: payload.analysis.keyPalaces,
      palaces: (payload.analysis.palaces || []).map((p: any) => ({
        num: p.num,
        menRel: p.menRel,
        keying: (p.keying || []).slice(0, 4)
      })),
      harms: (payload.analysis.harms || []).slice(0, 24),
      timing: (payload.analysis.timing || []).slice(0, 10),
      score: payload.analysis.score,
      tone: payload.analysis.tone
    };
    body.analysis = slim;
  }
  if (payload.mode === 'ask') {
    body.question = payload.question;
  }
  const token = await tokenOf(user);
  const res = await apiFetch('/ai/qimen-ask', { method: 'POST', token, body: JSON.stringify(body) });
  return {
    data: res?.data ?? {},
    sessionId: res?.metadata?.sessionId ?? null,
    title: res?.metadata?.title ?? '',
    model: res?.metadata?.model
  };
}

export async function listQimenSessions(user: User | null): Promise<QimenSessionSummary[]> {
  if (!user) return [];
  const token = await tokenOf(user);
  const res = await apiFetch(`/web/qimen-history/${user.uid}`, { method: 'GET', token });
  return (res?.data ?? []) as QimenSessionSummary[];
}

export async function getQimenSession(user: User | null, sessionId: string): Promise<QimenSessionFull | null> {
  if (!user) return null;
  const token = await tokenOf(user);
  try {
    const res = await apiFetch(`/web/qimen-session/${user.uid}/${sessionId}`, { method: 'GET', token });
    return (res?.data ?? null) as QimenSessionFull | null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function deleteQimenSession(user: User | null, sessionId: string): Promise<void> {
  if (!user) return;
  const token = await tokenOf(user);
  await apiFetch(`/web/qimen-session/${user.uid}/${sessionId}`, { method: 'DELETE', token });
}

/** 历史页「去奇门页继续」：把会话快照写入交接缓存，由 QimenPage 消费 */
export function stashQimenRestoreSession(session: QimenSessionFull): void {
  try {
    localStorage.setItem('qm-ai-restore', JSON.stringify({ chart: session.chart, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}
